import React, { useState, useEffect, useRef } from 'react';
import { HiChevronDown, HiCheck } from 'react-icons/hi';
import AppLayout from '../../components/layout/AppLayout';
import Button from '../../components/ui/Button';
import FormInput from '../../components/forms/FormInput';
import {
  getApartments,
  getLatestReading,
  storeReading,
  getGasReadings,
  exportReadings,
  getGasSettings,
} from '../../api/gasReadings';

const TOWERS = ['A', 'B'];
const FLOORS = Array.from({ length: 17 }, (_, i) => i + 2);
const MONTHS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i + 1),
  label: new Date(2024, i).toLocaleString('default', { month: 'short' }),
}));
const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1;
const YEARS = Array.from({ length: CURRENT_YEAR - 2020 + 1 }, (_, i) => 2020 + i).map((y) => ({ value: String(y), label: String(y) }));

function getAvailableMonths(selectedYear) {
  const maxMonth = selectedYear === CURRENT_YEAR ? CURRENT_MONTH : 12;
  return MONTHS.slice(0, maxMonth);
}
const TOWER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'A', label: 'Tower A' },
  { value: 'B', label: 'Tower B' },
];

function GridDropdown({ label, placeholder, value, displayValue, options, onChange, columns = 6, smColumns = 9 }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const gridCols = {
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
    6: 'grid-cols-6',
    7: 'grid-cols-7',
  };
  const smGridCols = {
    4: 'sm:grid-cols-4',
    5: 'sm:grid-cols-5',
    6: 'sm:grid-cols-6',
    7: 'sm:grid-cols-7',
    8: 'sm:grid-cols-8',
    9: 'sm:grid-cols-9',
  };

  return (
    <div className="relative" ref={ref}>
      {label && <label className="block text-sm font-semibold text-gray-900 mb-2">{label}</label>}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
          open
            ? 'border-primary-500 ring-4 ring-primary-500/10 bg-white'
            : value
              ? 'border-primary-200 bg-primary-50 text-primary-800'
              : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
        }`}
      >
        <span className={value ? 'text-gray-900 font-semibold' : ''}>{displayValue || placeholder}</span>
        <HiChevronDown className={`w-5 h-5 transition-transform ${open ? 'rotate-180' : ''} ${value ? 'text-primary-600' : 'text-gray-400'}`} />
      </button>

      {open && (
        <div className="absolute z-20 left-0 right-0 mt-2 bg-white rounded-xl border border-gray-200 shadow-lg p-3 animate-in fade-in slide-in-from-top-1">
          <div className={`grid ${gridCols[columns] || 'grid-cols-6'} ${smGridCols[smColumns] || 'sm:grid-cols-9'} gap-1.5`}>
            {options.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  onClick={() => { onChange(opt.value); setOpen(false); }}
                  className={`relative py-2.5 px-1 rounded-lg text-sm font-medium transition-all ${
                    isSelected
                      ? 'bg-primary-700 text-white shadow-sm'
                      : 'bg-gray-50 text-gray-700 hover:bg-primary-50 hover:text-primary-700'
                  }`}
                >
                  {opt.label}
                  {isSelected && (
                    <HiCheck className="absolute top-0.5 right-0.5 w-3 h-3" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function RecordReadingTab() {
  const [tower, setTower] = useState('');
  const [floor, setFloor] = useState('');
  const [apartments, setApartments] = useState([]);
  const [selectedApartment, setSelectedApartment] = useState(null);
  const [latestReading, setLatestReading] = useState(null);
  const [currentReading, setCurrentReading] = useState('');
  const [readingDate, setReadingDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [settings, setSettings] = useState({ m3_to_kg_factor: 1.724, price_per_kg: 45 });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getGasSettings().then((res) => {
      if (res.data) setSettings(res.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (tower && floor) {
      setLoading(true);
      getApartments({ tower, floor }).then((res) => {
        setApartments(res.data || []);
      }).catch(() => {}).finally(() => setLoading(false));
    } else {
      setApartments([]);
    }
    setSelectedApartment(null);
    setLatestReading(null);
    setCurrentReading('');
  }, [tower, floor]);

  const handleApartmentSelect = async (apt) => {
    setSelectedApartment(apt);
    setCurrentReading('');
    setMessage('');
    try {
      const res = await getLatestReading(apt.id);
      setLatestReading(res.data);
    } catch {
      setLatestReading(null);
    }
  };

  const previousM3 = latestReading ? parseFloat(latestReading.current_reading_m3) : 0;
  const currentM3 = parseFloat(currentReading) || 0;
  const consumptionM3 = currentM3 - previousM3;
  const factor = parseFloat(settings.m3_to_kg_factor) || 1.724;
  const pricePerKg = parseFloat(settings.price_per_kg) || 45;
  const consumptionKg = consumptionM3 * factor;
  const totalPrice = consumptionKg * pricePerKg;

  const handleSave = async () => {
    if (!selectedApartment || !currentReading) return;
    setSaving(true);
    setMessage('');
    try {
      await storeReading({
        apartment_id: selectedApartment.id,
        current_reading_m3: parseFloat(currentReading),
        reading_date: readingDate,
        notes: notes || null,
      });
      setMessage('Reading saved successfully!');
      setCurrentReading('');
      setNotes('');
      setSelectedApartment(null);
      setLatestReading(null);
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to save reading');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {message && (
        <div className={`px-4 py-2 rounded-lg text-sm ${message.includes('success') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message}
        </div>
      )}

      {/* Tower Selection */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Select Tower</h3>
        <div className="flex gap-3">
          {TOWERS.map((t) => (
            <button
              key={t}
              onClick={() => { setTower(t); setFloor(''); }}
              className={`flex-1 sm:flex-none px-6 sm:px-8 py-3 rounded-lg text-sm font-semibold transition-all ${
                tower === t
                  ? 'bg-primary-700 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Tower {t}
            </button>
          ))}
        </div>
      </div>

      {/* Floor & Apartment Selection */}
      {tower && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <GridDropdown
              label="Floor"
              placeholder="Select Floor"
              value={floor}
              displayValue={floor ? `Floor ${floor}` : ''}
              options={FLOORS.map((f) => ({ value: String(f), label: String(f) }))}
              onChange={(val) => setFloor(val)}
              columns={6}
              smColumns={9}
            />
            <div>
              {floor ? (
                loading ? (
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Apartment</label>
                    <div className="flex items-center justify-center py-3 rounded-xl border-2 border-gray-200 bg-gray-50 text-sm text-gray-400">
                      Loading...
                    </div>
                  </div>
                ) : (
                  <GridDropdown
                    label="Apartment"
                    placeholder="Select Apartment"
                    value={selectedApartment ? String(selectedApartment.id) : ''}
                    displayValue={selectedApartment ? `${selectedApartment.unit} — ${selectedApartment.owner_name || 'No owner'}` : ''}
                    options={apartments.map((apt) => ({ value: String(apt.id), label: apt.unit }))}
                    onChange={(val) => {
                      const apt = apartments.find((a) => String(a.id) === val);
                      if (apt) handleApartmentSelect(apt);
                    }}
                    columns={4}
                    smColumns={7}
                  />
                )
              ) : (
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Apartment</label>
                  <div className="flex items-center justify-center py-3 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 text-sm text-gray-400">
                    Select a floor first
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reading Form */}
      {selectedApartment && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">
            Apartment {selectedApartment.unit} — Tower {tower}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <p className="text-xs text-gray-500 mb-1">Owner</p>
              <p className="text-sm font-medium text-gray-900">{selectedApartment.owner_name || 'N/A'}</p>
              {selectedApartment.owner_phone && (
                <p className="text-xs text-gray-500 mt-1">{selectedApartment.owner_phone}</p>
              )}
            </div>
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <p className="text-xs text-gray-500 mb-1">Previous Reading</p>
              <p className="text-sm font-medium text-gray-900">{previousM3.toFixed(3)} m³</p>
              {latestReading && (
                <p className="text-xs text-gray-500 mt-1">Date: {latestReading.reading_date}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
            <FormInput
              label="Current Reading (m³)"
              type="number"
              step="0.001"
              value={currentReading}
              onChange={(e) => setCurrentReading(e.target.value)}
              required
            />
            <FormInput
              label="Reading Date"
              type="date"
              value={readingDate}
              onChange={(e) => setReadingDate(e.target.value)}
              required
            />
          </div>

          <FormInput
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mb-4"
          />

          {currentReading && consumptionM3 >= 0 && (
            <div className="bg-blue-50 rounded-lg p-3 sm:p-4 mb-4">
              <h4 className="text-xs font-semibold text-blue-800 mb-2">Calculated Values</h4>
              <div className="grid grid-cols-3 gap-2 sm:gap-4 text-sm">
                <div>
                  <p className="text-[10px] sm:text-xs text-blue-600">Consumption (m³)</p>
                  <p className="font-semibold text-blue-900 text-sm sm:text-base">{consumptionM3.toFixed(3)}</p>
                </div>
                <div>
                  <p className="text-[10px] sm:text-xs text-blue-600">Consumption (kg)</p>
                  <p className="font-semibold text-blue-900 text-sm sm:text-base">{consumptionKg.toFixed(3)}</p>
                </div>
                <div>
                  <p className="text-[10px] sm:text-xs text-blue-600">Total Price</p>
                  <p className="font-semibold text-blue-900 text-sm sm:text-base">₹{totalPrice.toFixed(2)}</p>
                </div>
              </div>
            </div>
          )}

          <Button onClick={handleSave} loading={saving} disabled={!currentReading || consumptionM3 < 0}>
            Save Reading
          </Button>
        </div>
      )}
    </div>
  );
}

function ReadingHistoryTab() {
  const [tower, setTower] = useState('');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchReadings = async () => {
    setLoading(true);
    try {
      const params = { month, year };
      if (tower) params.tower = tower;
      const res = await getGasReadings(params);
      setReadings(res.data || []);
    } catch {
      setReadings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReadings();
  }, [tower, month, year]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <GridDropdown
            label="Year"
            placeholder="Select Year"
            value={String(year)}
            displayValue={String(year)}
            options={YEARS}
            onChange={(val) => {
              const y = parseInt(val);
              setYear(y);
              if (y === CURRENT_YEAR && month > CURRENT_MONTH) setMonth(CURRENT_MONTH);
            }}
            columns={4}
            smColumns={7}
          />
          <GridDropdown
            label="Month"
            placeholder="Select Month"
            value={String(month)}
            displayValue={MONTHS.find((m) => m.value === String(month))?.label || ''}
            options={getAvailableMonths(year)}
            onChange={(val) => setMonth(parseInt(val))}
            columns={4}
            smColumns={6}
          />
          <GridDropdown
            label="Tower"
            placeholder="All Towers"
            value={tower}
            displayValue={tower ? `Tower ${tower}` : 'All Towers'}
            options={TOWER_OPTIONS}
            onChange={(val) => setTower(val)}
            columns={3}
            smColumns={3}
          />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500 text-sm">Loading...</div>
        ) : readings.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">No readings found for this period.</div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Unit</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Owner</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">Previous (m³)</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">Current (m³)</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">Consumption (m³)</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">kg</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">Price (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {readings.map((r) => (
                    <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{r.apartment?.unit}</td>
                      <td className="px-4 py-3 text-gray-600">{r.apartment?.owner_name || '—'}</td>
                      <td className="px-4 py-3 text-right">{parseFloat(r.previous_reading_m3).toFixed(3)}</td>
                      <td className="px-4 py-3 text-right">{parseFloat(r.current_reading_m3).toFixed(3)}</td>
                      <td className="px-4 py-3 text-right">{parseFloat(r.consumption_m3).toFixed(3)}</td>
                      <td className="px-4 py-3 text-right">{parseFloat(r.consumption_kg).toFixed(3)}</td>
                      <td className="px-4 py-3 text-right font-medium">₹{parseFloat(r.total_price).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <div className="sm:hidden divide-y divide-gray-100">
              {readings.map((r) => (
                <div key={r.id} className="p-4 space-y-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-gray-900">{r.apartment?.unit}</p>
                      <p className="text-xs text-gray-500">{r.apartment?.owner_name || '—'}</p>
                    </div>
                    <p className="text-sm font-semibold text-primary-700">₹{parseFloat(r.total_price).toFixed(2)}</p>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <p className="text-gray-400">Prev (m³)</p>
                      <p className="font-medium text-gray-700">{parseFloat(r.previous_reading_m3).toFixed(3)}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Curr (m³)</p>
                      <p className="font-medium text-gray-700">{parseFloat(r.current_reading_m3).toFixed(3)}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Cons (kg)</p>
                      <p className="font-medium text-gray-700">{parseFloat(r.consumption_kg).toFixed(3)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ExportTab() {
  const [tower, setTower] = useState('');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState('');

  const handleExport = async () => {
    setExporting(true);
    setMessage('');
    try {
      const params = { month, year };
      if (tower) params.tower = tower;
      const response = await exportReadings(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gas_readings_${year}_${String(month).padStart(2, '0')}${tower ? `_tower_${tower}` : ''}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setMessage('Export downloaded successfully!');
    } catch {
      setMessage('Failed to export readings.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Export Gas Readings</h3>
      {message && (
        <div className={`px-4 py-2 rounded-lg text-sm mb-4 ${message.includes('success') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <GridDropdown
          label="Year"
          placeholder="Select Year"
          value={String(year)}
          displayValue={String(year)}
          options={YEARS}
          onChange={(val) => {
            const y = parseInt(val);
            setYear(y);
            if (y === CURRENT_YEAR && month > CURRENT_MONTH) setMonth(CURRENT_MONTH);
          }}
          columns={4}
          smColumns={7}
        />
        <GridDropdown
          label="Month"
          placeholder="Select Month"
          value={String(month)}
          displayValue={MONTHS.find((m) => m.value === String(month))?.label || ''}
          options={getAvailableMonths(year)}
          onChange={(val) => setMonth(parseInt(val))}
          columns={4}
          smColumns={6}
        />
        <GridDropdown
          label="Tower"
          placeholder="All Towers"
          value={tower}
          displayValue={tower ? `Tower ${tower}` : 'All Towers'}
          options={TOWER_OPTIONS}
          onChange={(val) => setTower(val)}
          columns={3}
          smColumns={3}
        />
      </div>
      <Button onClick={handleExport} loading={exporting}>Export to Excel</Button>
    </div>
  );
}

const TABS = [
  { key: 'record', label: 'Record' },
  { key: 'history', label: 'History' },
  { key: 'export', label: 'Export' },
];

export default function GasReadingsPage() {
  const [activeTab, setActiveTab] = useState('record');

  return (
    <AppLayout title="Gas Readings" breadcrumbs="Gas Readings">
      <div className="mb-4 sm:mb-6">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-full sm:w-fit">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === tab.key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'record' && <RecordReadingTab />}
      {activeTab === 'history' && <ReadingHistoryTab />}
      {activeTab === 'export' && <ExportTab />}
    </AppLayout>
  );
}
